import { Options } from '@contentful/rich-text-react-renderer';
import { ContentfulRichTextGatsbyReference } from 'gatsby-source-contentful/rich-text';

const removeTrailingWhitespaceNodes = (nodes: ContentRawParsed['content']) => {
  while (nodes.length > 0) {
    const lastNode = nodes[nodes.length - 1];

    if (
      lastNode.nodeType === 'text' &&
      typeof lastNode.value === 'string' &&
      lastNode.value.trim() === ''
    ) {
      nodes.pop();
    } else {
      break;
    }
  }

  return nodes;
};

const removeEmptyTextNodes = (nodes: ContentRawParsed[]) =>
  nodes.filter((node) => {
    node.content = removeTrailingWhitespaceNodes(node.content);

    return !(node.nodeType === 'paragraph' && node.content.length === 0);
  });

const processRaw = (rawContent: string) => {
  const raw: ContentRawParsed = JSON.parse(rawContent);

  return {
    ...raw,
    content: removeEmptyTextNodes(raw.content),
  };
};

const processReferences = (references: ContentReference[]) =>
  references.map((ref) => {
    switch (ref.__typename) {
      case 'ContentfulCallToActionModule':
        ref.actions = ref.actions.map((action) => {
          const raw: ContentRawParsed = JSON.parse(action.text.raw);

          raw.content = removeEmptyTextNodes(raw.content);
          action.text.raw = JSON.stringify(raw);

          return action;
        });

        break;

      case 'ContentfulFactsModule':
        ref.facts = ref.facts.map((fact) => {
          const raw: ContentRawParsed = JSON.parse(fact.content.raw);

          raw.content = removeEmptyTextNodes(raw.content);
          fact.content.raw = JSON.stringify(raw);

          return fact;
        });

        break;

      case 'ContentfulFaqModule':
        ref.faqs = ref.faqs.map((faq) => {
          const raw: ContentRawParsed = JSON.parse(faq.answer.raw);

          raw.content = removeEmptyTextNodes(raw.content);
          faq.answer.raw = JSON.stringify(raw);

          return faq;
        });

        break;

      case 'ContentfulElementCollapsibleContent': {
        const raw: ContentRawParsed = JSON.parse(ref.content.raw);

        raw.content = removeEmptyTextNodes(raw.content);
        ref.content.raw = JSON.stringify(raw);

        break;
      }

      case 'ContentfulAnchorTagNavigationPlaceholder':
        // populate anchor tag nav
        ref.teasers = references.filter(
          (item) => item.__typename === 'ContentfulAnchorTag',
        );

        break;

      default:
        break;
    }

    return ref;
  });

export type ContentRawParsed = {
  nodeType: string;
  content: {
    nodeType: string;
    content: ContentRawParsed['content'][0][];
    value?: string;
    resolution?: string;
    data?: { target?: { sys: { id: string } } };
    firstNode?: boolean;
    lastNode?: boolean;
  }[];
};

export type ContentReference = ContentfulRichTextGatsbyReference & {
  title?: string;
  content: { raw: string };
  actions: { text: { raw: string } }[];
  facts: { content: { raw: string } }[];
  faqs: { answer: { raw: string } }[];
  teasers: object[];
};

export type ContentProps = {
  content: {
    raw: string;
    references: ContentReference[];
  };
  options: Options;
  nested?: boolean;
};

export { processRaw, processReferences };
